import { useAppDispatch } from 'app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useAddDocumentMutation, useDeleteDocumentMutation } from 'services/documents';
import { useEditProductCategoryMutation, useGetProductCategoryWithDocumentQuery } from '../services';
import { useForm } from 'hooks/useForm';
import { PRODUCT_CATEGORY_FIELDS_SCHEMA, PRODUCT_CATEGORY_INITIAL_FIELDS } from '../constants/product-category-fields';
import { FormEvent, useCallback, useEffect } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { PRODUCT_CATEGORY_STATUS } from '../constants/status';
import { trimValues } from 'utils/object-helpers';
import { ProductCategory, ProductCategoryDto } from '../types/dtos';
import { dataURLtoFile } from 'utils/file-helpers';

const useController = () => {
    const { productCategoryId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editProductCategory, { isLoading: isEditProductCategoryLoading }] = useEditProductCategoryMutation();
    const [addDocument, { isLoading: isAddDocumentLoading }] = useAddDocumentMutation();
    const [deleteDocument, { isLoading: isDeleteDocumentLoading }] = useDeleteDocumentMutation();
    const { data: productCategory } = useGetProductCategoryWithDocumentQuery(productCategoryId as string);

    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: PRODUCT_CATEGORY_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });

    useEffect(() => {
        if (productCategory) {
            const initialCategory = {
                ...productCategory,
                isActive: productCategory.isActive ? PRODUCT_CATEGORY_STATUS.ACTIVE : PRODUCT_CATEGORY_STATUS.INACTIVE,
            };
            setInitialFormData({ ...PRODUCT_CATEGORY_INITIAL_FIELDS, ...initialCategory });
        }
    }, [productCategory, setInitialFormData]);

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'productCategory.PRODUCT_CATEGORY_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(
        (documentId?: string) => {
            const data = { ...changedValues };
            if (documentId) data.documentId = documentId;
            delete data.logo;

            data.isActive = data?.isActive === PRODUCT_CATEGORY_STATUS.ACTIVE ? true : false;
            return trimValues(data) as ProductCategoryDto;
        },
        [changedValues],
    );

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                let documentId: string | undefined;
                const prevDocument = productCategory?.documentId;
                try {
                    if (changedValues?.logo) {
                        documentId = (await addDocument(dataURLtoFile(changedValues.logo)).unwrap()).documentId;
                    }

                    const payload = getPayload(documentId);
                    await editProductCategory({ id: productCategoryId as string, payload }).unwrap();

                    if (changedValues?.logo && prevDocument) {
                        await deleteDocument(prevDocument).unwrap();
                    }
                    handleSuccess();

                    navigate(-1);
                } catch (error) {
                    if (documentId) await deleteDocument(documentId).unwrap();
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            addDocument,
            changedValues,
            productCategory?.documentId,
            deleteDocument,
            editProductCategory,
            getPayload,
            handleErrors,
            handleSubmit,
            handleSuccess,
            productCategoryId,
            navigate,
        ],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as ProductCategory,
        onChange: handleChange,
        onSubmit,
        onCancel,
        dataChanged: isDirty,
        isLoading: formData === undefined,
        isEditProductCategoryLoading: isEditProductCategoryLoading || isAddDocumentLoading || isDeleteDocumentLoading,
        errors,
    };
};

export default useController;
